
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator';
import { Button, Toast, Loading, Icon, ImagePreview } from 'vant';
import EXIF from 'exif-js';
import OSS from 'ali-oss';
import { FileType } from '@/common/ts/enum';
import { genHash, isWeiXin } from '@/common/ts/common';
import { getOssSignature } from '@/api/api';
import Cropper from 'simple-crop/index.vue';
import 'simple-crop/template-2.css';
import axios from 'axios';
Vue.use(Button).use(Toast).use(Loading).use(Icon);

const reader = new FileReader();
let canvas: HTMLCanvasElement;
let context: CanvasRenderingContext2D;

const imgCdnPrefix = process.env.VUE_APP_OSS_IMG;

enum FileStatus {
  waiting = 50,
  uploading = 100,
  success = 200,
  fail = 500,
}

interface IPresetFiles {
  cover: string;
  path: string;
  type: FileType;
}

interface IDisplayImageItem {
  url: string;
  status: FileStatus;
}
interface IWaitingToUploadItem {
  file: Blob | File;
  name: string;
}
interface IProcessedImage {
  fileData: IWaitingToUploadItem;
  base64: string;
}

@Component({
  components: {
    Cropper,
  },
})
export default class Uploader extends Vue {
  @Prop({ default: FileType.Image }) fileType!: FileType;
  @Prop({ default: Array }) presetFiles!: IPresetFiles[]; // 预设文件展示
  @Prop({ default: 9 }) maxImage!: number; // 默认可上传最多9张图片
  @Prop({ default: 1 }) maxVideo!: number; // 默认上传单个视频
  @Prop({ default: 100 }) videoMaxSize!: number; // 默认限制100MB视频上传限制
  @Prop({ default: 10 }) imageMaxSize!: number; // 默认限制10MB图片上传限制
  @Prop({ default: 0 }) imageMinSize!: number; // 默认限制最小上传10kb
  @Prop({ type: Function }) getOssSignatureFn!: any; // 自定义的oss签名函数
  @Prop({ type: String, default: '' }) customDir!: string;
  @Prop({ type: Boolean, default: false }) mustInApp!: boolean;
  @Prop({ type: Boolean, default: false }) needCompress!: boolean; // 是否需要压缩
  @Prop({ default: null }) cropConfig!: any; // 是否裁剪{width, height}
  @Prop({ default: true }) mustSelectFile!: boolean; // 是否验证必选文件
  @Prop({ default: false }) immediately!: boolean; // 选择文件后立即上传
  @Prop({ default: 2 }) targetImageSize!: number; // 图片压缩后的大小期望值，即最大不超过多少，默认2MB
  @Prop({ default: false }) fixOrientation!: boolean; // 默认修复图片旋转bug
  @Prop({ default: 'image/*' }) accept!: string; // 默认修复图片旋转bug
  @Prop({ default: true }) isWap!: boolean; // 默认修复图片旋转bug
  @Prop({ default: 0 }) itemIdx!: number; // 默认下标
  // data
  displayImages: IDisplayImageItem[] = [];
  readyToUploadQueue: IWaitingToUploadItem[] = [];
  FileType = FileType;
  FileStatus = FileStatus;
  showUploaderBtn = true;
  formData: any = [];
  cropperInitialized = false;
  cropParams: any = {};
  currentFile: File | undefined = undefined;
  userHasUpload = false; // 控制预设图片的状态蒙层

  @Watch('presetFiles.length')
  onPresetFilesChange(val: any, oldval: any) {
    // 处理含预设上传图片时，剩余文件上传数量的控制
    if (
      (this.fileType === FileType.Image &&
        this.presetFiles.length >= this.maxImage) ||
      (this.fileType === FileType.Video &&
        this.presetFiles.length >= this.maxVideo)
    ) {
      this.showUploaderBtn = false;
    }
  }

  mounted() {
    // 初始化canvas
    canvas = document.createElement('canvas');
    context = canvas.getContext('2d')!;
    // 处理含预设上传图片时，剩余文件上传数量的控制
    if (
      this.fileType === FileType.Image &&
      this.presetFiles.length >= this.maxImage
    ) {
      this.showUploaderBtn = false;
    } else if (
      this.fileType === FileType.Video &&
      this.presetFiles.length >= this.maxVideo
    ) {
      this.showUploaderBtn = false;
    } else {
      this.showUploaderBtn = true;
    }
  }

  // 上传阿里云
  uploadToOss() {
    return new Promise((resolve, reject) => {
      // 客户端验证
      if (!this.mustInApp) {
        console.log('need not in app');
      } else if (!window.udid && !isWeiXin()) {
        return Toast('请在上游客户端或微信参与');
      }
      // 是否选择文件验证
      console.log(this.readyToUploadQueue.length);
      if (
        !this.readyToUploadQueue.length &&
        !this.presetFiles.length &&
        this.mustSelectFile
      ) {
        return Toast('未选择文件');
      }

      Promise.all(
        this.readyToUploadQueue.map((item, index) => {
          return this.uploadOssWithSignature(item, index);
        }),
      )
        .then((result) => {
          // Toast.success('上传图片成功');
          // Toast.clear();

          if (this.presetFiles && this.presetFiles.length) {
            const temp = this.presetFiles.map((item) => item.path);
            resolve([...temp, ...result]);
          } else {
            resolve([...result]);
          }
        })
        .catch((err) => {
          console.log(err);
          Toast.fail(`上传失败:${err}`);
          // Toast.clear();
        });
    });
  }

  getFileStatusText(status: FileStatus) {
    let text = '';
    switch (status) {
      case FileStatus.waiting:
        text = '';
        break;
      case FileStatus.uploading:
        text = '上传中...';
        break;
      case FileStatus.success:
        text = '已上传';
        break;
      case FileStatus.fail:
        text = '上传失败';
        break;
      default:
        text = '';
        break;
    }
    return text;
  }

  @Watch('readyToUploadQueue')
  onUploadImagesChange(newval: any[], oldval: any[]) {
    if (
      this.fileType === FileType.Image &&
      newval.length + this.presetFiles.length >= this.maxImage
    ) {
      this.showUploaderBtn = false;
      if (this.immediately) {
        this.uploadToOss().then((res) => {
          this.$emit('getData', res);
        });
      }
    } else if (
      this.fileType === FileType.Video &&
      newval.length + this.presetFiles.length >= this.maxVideo
    ) {
      this.showUploaderBtn = false;
    } else {
      this.showUploaderBtn = true;
    }
  }

  // 获取图片base64Url
  getImageFileLocalUrl(e: InputEvent) {
    const payloadFiles = (e.target as HTMLInputElement).files!;

    if (!payloadFiles.length) {
      return console.log('未选取文件');
    }
    this.currentFile = payloadFiles[0];
    const payloadFileSize = this.currentFile.size / 1024 / 1024;

    if (payloadFileSize > this.imageMaxSize) {
      return Toast(`上传图片最大限制${this.imageMaxSize}MB`);
    } else if (payloadFileSize < this.imageMinSize) {
      return Toast(`上传图片最小限制${this.imageMinSize}MB`);
    }

    if (this.displayImages.length + payloadFiles.length > this.maxImage) {
      return Toast('图片只能上传' + this.maxImage + '张');
    }

    // *文件为gif格式以及在显式指定不压缩的情况下不做任何处理
    if (this.currentFile.type === 'image/gif' || !this.needCompress) {
      const previewURL = window.URL.createObjectURL(this.currentFile);
      this.displayImages.push({
        url: previewURL,
        status: FileStatus.waiting,
      });
      this.readyToUploadQueue.push({
        file: this.currentFile,
        name: this.currentFile.name,
      });
    } else if (this.cropConfig) {
      reader.readAsDataURL(this.currentFile);
      reader.onloadend = () => {
        // * 裁剪图片逻辑
        this.cropParams = {
          src: reader.result, // 裁剪图片地址
          size: {
            // 裁剪尺寸
            width: this.cropConfig.width,
            height: this.cropConfig.height,
          },
          borderColor: '#fff', // 裁剪框边框颜色
          cropSizePercent: 0.9, // 裁剪框显示比例
        };
        this.cropperInitialized = true;
      };
    } else {
      // * 图片修正旋转及压缩操作
      reader.readAsDataURL(this.currentFile);
      reader.onloadend = async () => {
        Toast.loading({
          message: '图片处理中...',
        });

        const imgUrlFixedOrientation = await this.normalizeImageRotaion(
          reader.result as string,
        );
        // * 需要压缩图片
        const compressedFile = await this.compressImage(
          this.fixOrientation
            ? imgUrlFixedOrientation.base64
            : (reader.result as string),
        );

        this.displayImages.push({
          url: compressedFile.base64,
          status: FileStatus.waiting,
        });

        this.readyToUploadQueue = [
          ...this.readyToUploadQueue,
          compressedFile.fileData,
        ];
        Toast.clear();
      };
    }
    if (this.displayImages.length >= this.maxImage) {
      this.showUploaderBtn = false;
    }
  }
  // 处理图片旋转
  normalizeImageRotaion(imgUrl: string) {
    return new Promise<IProcessedImage>((resolve, reject) => {
      const img = new Image();

      img.src = imgUrl;
      img.onload = () => {
        const originWidth = img.width;
        const originHeight = img.height;

        canvas.width = originWidth;
        canvas.height = originHeight;
        context.clearRect(0, 0, canvas.width, canvas.height);
        // 统一将背景填充为白色，避免PNG转JPEG时背景变黑
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        EXIF.getData(this.currentFile as any, () => {
          const Orientation = EXIF.getTag(this.currentFile, 'Orientation');
          console.log(
            '%c 图片文件Orientation信息',
            'color: orange',
            Orientation,
          );
          if (Orientation && Orientation !== 1) {
            switch (Orientation) {
              case 6: // 旋转90度
                canvas.width = originHeight;
                canvas.height = originWidth;
                context.rotate(Math.PI / 2);
                // (0,-imgHeight) 从旋转原理图那里获得的起始点
                context.drawImage(
                  img,
                  0,
                  -originHeight,
                  originWidth,
                  originHeight,
                ); // 关键语句
                break;
              case 3: // 旋转180度
                context.rotate(Math.PI);
                context.drawImage(
                  img,
                  -originWidth,
                  -originHeight,
                  originWidth,
                  originHeight,
                );
                break;
              case 8: // 旋转-90度
                canvas.width = originHeight;
                canvas.height = originWidth;
                context.rotate((3 * Math.PI) / 2);
                context.drawImage(
                  img,
                  -originWidth,
                  0,
                  originWidth,
                  originHeight,
                );
                break;
            }
          } else {
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
          }
          canvas.toBlob((blob) => {
            resolve({
              base64: canvas.toDataURL('image/jpeg'),
              fileData: {
                file: blob!,
                name: this.currentFile!.name,
              },
            });
          }, 'image/jpeg');
        });
      };
    });
  }
  getFileSize(canvas: HTMLCanvasElement, quality = 0.92) {
    const canvasURL = canvas.toDataURL('image/jpeg', quality);
    const buffer = atob(canvasURL.split(',')[1]);
    return buffer.length;
  }
  // 处理图片压缩
  compressImage(imgUrl: string) {
    // * 压缩期望值以及阈值
    let quality = 0.9; // toDataUrl和toBlob方法中的quality默认值为 0.92
    let scale = 1;

    const minQuality = 0.5;
    const minScale = 0.5;
    const expectedSize = 1024 * 1024 * this.targetImageSize;
    const qualityDecreaseStep = 0.05;
    const scaleDecreaseStep = 0.1;

    return new Promise<IProcessedImage>((resolve, reject) => {
      const img = new Image();
      img.src = imgUrl;
      img.onload = () => {
        // 缩放图片需要的canvas
        canvas.width = img.width;
        canvas.height = img.height;
        // 清除画布
        context.clearRect(0, 0, canvas.width, canvas.height);
        // 统一将背景填充为白色，避免PNG转JPEG时背景变黑
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.drawImage(img, 0, 0, canvas.width, canvas.height);

        const minimizerByQuality = () => {
          const fileSize = this.getFileSize(canvas, quality);
          console.log(
            '~~~~~current file size is:',
            fileSize,
            'and quality is:',
            quality,
            'expected size is:',
            expectedSize,
          );
          if (fileSize > expectedSize && quality > minQuality) {
            console.log('compress again');
            quality = Number((quality - qualityDecreaseStep).toFixed(2));
            minimizerByQuality();
          } else if (fileSize > expectedSize && quality <= minQuality) {
            console.log('进行尺寸缩放压缩');
            minimizerByScale();
          } else {
            console.log('压缩成功');
            canvas.toBlob(
              (blob) => {
                resolve({
                  base64: canvas.toDataURL('image/jpeg', quality),
                  fileData: {
                    file: blob!,
                    name: this.currentFile!.name,
                  },
                });
              },
              'image/jpeg',
              quality,
            );
          }
        };
        const minimizerByScale = () => {
          scale = scale - scaleDecreaseStep;
          canvas.width = canvas.width * scale;
          canvas.height = canvas.height * scale;

          // 清除画布
          context.clearRect(0, 0, canvas.width, canvas.height);
          // 统一将背景填充为白色，避免PNG转JPEG时背景变黑
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(img, 0, 0, canvas.width, canvas.height);

          const fileSize = this.getFileSize(canvas, quality);

          console.log(
            '====>>>>',
            'current file size is:',
            fileSize,
            'and quality is:',
            quality,
            'expected size is:',
            expectedSize,
          );

          if (fileSize > expectedSize && scale > minScale) {
            console.log('compress again by scale decrease');
            minimizerByScale();
          } else if (fileSize > expectedSize && scale <= minScale) {
            reject('已经无法进一步压缩');
          } else {
            console.log('压缩成功');
            canvas.toBlob(
              (blob) => {
                resolve({
                  base64: canvas.toDataURL('image/jpeg', quality),
                  fileData: {
                    name: this.currentFile!.name,
                    file: blob!,
                  },
                });
              },
              'image/jpeg',
              quality,
            );
          }
        };
        minimizerByQuality();
      };
    });
  }
  // 获取视频文件信息
  getVideoFileInfo(e: any) {
    if (!e.target.files.length) {
      return;
    }
    const videoReader = new FileReader();
    const videoFile = e.target.files[0];
    videoReader.readAsArrayBuffer(videoFile);

    videoReader.onloadend = (x: any) => {
      // 判断视频是否超过指定大小
      const videoSize = Math.round(videoFile.size / 1024 / 1024);
      if (videoSize > this.videoMaxSize) {
        return Toast(`上传视频文件限制${this.videoMaxSize}MB`);
      }
      const blob = new Blob([new Uint8Array(x.currentTarget.result)], {
        type: videoFile.type,
      });
      this.readyToUploadQueue.push({
        file: blob,
        name: videoFile.name,
      });
      this.displayImages.push({
        url: require('@/assets/video.png'),
        status: FileStatus.waiting,
      });
    };
  }

  // 移除所选图片
  removeImage(index: number, type: 'added' | 'preset') {
    if (type === 'added') {
      this.displayImages.splice(index, 1);
      this.readyToUploadQueue.splice(index, 1);
      // (this.$refs.inputImage as HTMLInputElement).value = '';

      if (this.fileType === FileType.Image) {
        if (this.showUploaderBtn) {
          (this.$refs.inputImage as HTMLInputElement).value = '';
        }
      } else {
        if (this.showUploaderBtn) {
          (this.$refs.inputVideo as HTMLInputElement).value = '';
        }
      }
    } else if (this.presetFiles.length && type === 'preset') {
      this.presetFiles.splice(index, 1);
    }
    this.showUploaderBtn = true;
    this.$emit('remove', this.itemIdx, index);
  }
  generateRandomString(length = 16): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
  async uploadOssWithSignature(fileData: any, queueIndex: number) {
    const ossRes: any = await getOssSignature();
    const { policy, accessid, callback, signature, host, dir } = ossRes.data;

    const formData = new FormData();
    const name = fileData.name
      .split('.')
      [fileData.name.split('.').length - 1].toLowerCase();
    const uploadType = this.generateRandomString(10) + '.' + name;
    const key = `${dir}${+new Date()}_${uploadType}`;
    formData.append('key', key);
    formData.append('policy', policy);
    formData.append('OSSAccessKeyId', accessid);
    formData.append('success_action_status', '200');
    formData.append('callback', callback);
    formData.append('signature', signature);
    formData.append('name', name); // 文件名
    formData.append('file', fileData.file);
    this.displayImages[queueIndex].status = FileStatus.uploading;
    return new Promise((resolve) => {
      return axios({
        url: `https://` + host,
        data: formData,
        method: 'post',
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then((res: any) => {
          resolve(imgCdnPrefix + key);
          this.displayImages[queueIndex].status = FileStatus.success;
        })
        .catch(() => {
          this.displayImages[queueIndex].status = FileStatus.fail;
        });
    });
  }
  // 图片裁剪
  onCrop(el: HTMLCanvasElement) {
    this.displayImages.push({
      url: el.toDataURL('image/*', 0.95),
      status: FileStatus.waiting,
    });
    el.toBlob((blob) => {
      const fileName = this.currentFile!.name;
      const blobFile: any = {};
      blobFile.name = fileName;
      blobFile.file = blob;
      this.readyToUploadQueue = [...this.readyToUploadQueue, blobFile];
    });
  }
  // 预览图片
  onUploadFileClick(fileUrl: string, type: FileType) {
    if (type === FileType.Image && this.isWap) {
      ImagePreview({
        images: [fileUrl],
        showIndicators: false,
        showIndex: false,
      });
    }
  }
}
